import React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Share2, Send, Globe, MoreHorizontal, Repeat2, MessageCircle, Bookmark } from 'lucide-react';
import { MediaDisplay } from './MediaDisplay';
import { PreviewProps, getFollowersCount, formatFollowersCount } from './types';

// Import reaction icons
import linkedinLike from '../../../assets/icons/linkedin-like.svg';
import linkedinLove from '../../../assets/icons/linkedin-love.svg';
import linkedinSupport from '../../../assets/icons/linkedin-support.svg';
import linkedinInsightful from '../../../assets/icons/linkedin-insightful.svg';
import linkedinFunny from '../../../assets/icons/linkedin-funny.svg';

// Helper function to check if URL is a video (blob-safe fallback using mime hints when present)
const isVideo = (url?: string): boolean => {
  if (!url) return false;
  if (url.startsWith('blob:')) {
    // Heuristic: if preview provides video blob, upstream should pass proper type; fallback to false
    return false;
  }
  const q = url.split('?')[0];
  const extension = q.split('.').pop()?.toLowerCase();
  return ['mp4', 'mov', 'webm', 'm4v'].includes(extension || '');
};

export const LinkedInPreview: React.FC<PreviewProps> = ({ content, brand, mediaUrl, mediaUrls, format = 'regular', hideEngagementCounts }) => {
  const { t } = useTranslation();
  
  // Récupérer le nombre de followers LinkedIn
  const linkedinFollowers = getFollowersCount(brand, 'linkedin');
  const formattedFollowers = formatFollowersCount(linkedinFollowers);

  const renderLinkedText = (text: string) => {
    const elements: React.ReactNode[] = [];
    const lines = (text || '').split('\n');
    const regex = /(https?:\/\/[^\s]+|www\.[^\s]+|#[\p{L}\d_]+)/giu;
    lines.forEach((line, lineIdx) => {
      if (lineIdx > 0) elements.push(<br key={`br-${lineIdx}`} />);
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = regex.exec(line)) !== null) {
        const start = match.index;
        if (start > lastIndex) {
          elements.push(line.slice(lastIndex, start));
        }
        const token = match[0];
        if (token.startsWith('#')) {
          elements.push(
            <span key={`hash-${lineIdx}-${start}`} className="text-[#0B65C2]">{token}</span>
          );
        } else {
          const href = token.startsWith('http') ? token : `http://${token}`;
          elements.push(
            <a key={`link-${lineIdx}-${start}`} href={href} target="_blank" rel="noreferrer" className="text-[#0B65C2] hover:underline">
              {token}
            </a>
          );
        }
        lastIndex = regex.lastIndex;
      }
      if (lastIndex < line.length) {
        elements.push(line.slice(lastIndex));
      }
    });
    return elements;
  };
  // Regular LinkedIn post
  if (format === 'regular') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-full text-sm">
        {/* Header */}
        <div className="p-2.5">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                {brand?.logo ? (
                  <img src={brand.logo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-200" />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 truncate text-sm">
                  {brand?.name || 'Company Name'}
                </h3>
                <p className="text-xs text-gray-500">{formattedFollowers} {t('previews.followers', 'followers')}</p>
                <p className="text-xs text-gray-500 flex items-center">
                  1h • <Globe className="w-2.5 h-2.5 ml-0.5" />
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-[#0b66c2] text-xs font-medium hover:underline cursor-pointer">
                + {t('previews.follow', 'Follow')}
              </span>
              <button className="p-0.5 hover:bg-gray-100 rounded-full">
                <MoreHorizontal className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-2.5 pb-2">
          <p className="text-gray-900 break-words" style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
            {renderLinkedText(content)}
          </p>
        </div>

        {/* Media */}
        {(mediaUrls && mediaUrls.length > 1) ? (
          <div className="px-2.5 pb-2">
            <div className="space-y-0.5">
              {/* First image large */}
              {(() => {
                const first = mediaUrls[0];
                return (
                  <div className="w-full bg-gray-100 aspect-video rounded-sm overflow-hidden">
                    <MediaDisplay
                      mediaUrl={first}
                      altText="LinkedIn media"
                      type="image"
                      className="w-full h-full"
                      objectFit="cover"
                    />
                  </div>
                );
              })()}
              {/* Second row with up to 3 images and +N overlay on last */}
              {(() => {
                const secondRow = mediaUrls.slice(1, 4);
                const remaining = Math.max(0, mediaUrls.length - 4);
                const cols = secondRow.length === 1 ? 'grid-cols-1' : secondRow.length === 2 ? 'grid-cols-2' : 'grid-cols-3';
                return (
                  <div className={`grid ${cols} gap-0.5`}>
                    {secondRow.map((src, idx) => (
                      <div key={`${src}-${idx}`} className="relative bg-gray-100 aspect-video rounded-sm overflow-hidden">
                        <MediaDisplay
                          mediaUrl={src}
                          altText={`LinkedIn media ${idx + 2}`}
                          type="image"
                          className="w-full h-full"
                          objectFit="cover"
                        />
                        {idx === secondRow.length - 1 && remaining > 0 && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="text-white text-[1.3rem] font-semibold">+{remaining}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        ) : (
          (mediaUrl || (mediaUrls && mediaUrls.length === 1)) && (
            <div className={mediaUrl?.includes('#pdfthumb') ? 'bg-gray-100 aspect-[4/5]' : 'bg-gray-100 aspect-video'}>
              <MediaDisplay
                mediaUrl={(mediaUrl || mediaUrls?.[0] || '').replace('#pdfthumb', '')}
                altText="LinkedIn media"
                type={isVideo(mediaUrl || mediaUrls?.[0] || '') ? 'video' : 'image'}
                className="w-full h-full"
                key={`${mediaUrl || mediaUrls?.[0]}-${isVideo(mediaUrl || mediaUrls?.[0] || '') ? 'video' : 'image'}`}
                aspectRatio={mediaUrl?.includes('#pdfthumb') ? '4 / 5' : undefined}
                objectFit={mediaUrl?.includes('#pdfthumb') ? 'contain' : 'cover'}
              />
            </div>
          )
        )}

        {/* Engagement Stats */}
        {!hideEngagementCounts && (
          <div className="px-2.5 pb-1.5 mt-1.5">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-0.5">
                <div className="flex -space-x-1">
                  <img src={linkedinLike} alt="Like" className="w-3.5 h-3.5" />
                  <img src={linkedinLove} alt="Love" className="w-3.5 h-3.5" />
                  <img src={linkedinSupport} alt="Support" className="w-3.5 h-3.5" />
                  <img src={linkedinInsightful} alt="Insightful" className="w-3.5 h-3.5" />
                  <img src={linkedinFunny} alt="Funny" className="w-3.5 h-3.5" />
                </div>
                <span>213</span>
              </div>
              <div className="flex items-center gap-1">
                <span>78 {t('previews.comments', 'comments')} • 121 {t('previews.reposts', 'reposts')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="px-1 py-0.5 border-t flex">
          <button className="flex-1 flex items-center justify-center gap-1 py-1.5 hover:bg-gray-100 rounded">
            <img src={linkedinLike} alt="Like" className="w-3.5 h-3.5" />
            <span className="text-xs font-medium text-gray-600">{t('previews.actions.like', 'Like')}</span>
          </button>

          <button className="flex-1 flex items-center justify-center gap-1 py-1.5 hover:bg-gray-100 rounded">
            <MessageSquare className="w-3.5 h-3.5" />
            <span className="text-xs font-medium text-gray-600">{t('previews.actions.comment', 'Comment')}</span>
          </button>

          <button className="flex-1 flex items-center justify-center gap-1 py-1.5 hover:bg-gray-100 rounded">
            <Repeat2 className="w-3.5 h-3.5 text-gray-600" strokeWidth={1.5} />
            <span className="text-xs font-medium text-gray-600">{t('previews.actions.repost', 'Repost')}</span>
          </button>

          <button className="flex-1 flex items-center justify-center gap-1 py-1.5 hover:bg-gray-100 rounded">
            <Send className="w-3.5 h-3.5" />
            <span className="text-xs font-medium text-gray-600">{t('previews.actions.send', 'Send')}</span>
          </button>
        </div>
      </div>
    );
  }
  
  // LinkedIn Shorts/Reels preview
  return (
    <div className="bg-black text-white rounded-xl shadow-md border border-gray-700 overflow-hidden w-full max-w-[320px] mx-auto">
      <div className="relative w-full aspect-[9/16] bg-gray-900 flex flex-col">
        {/* Main content */}
        <div className="flex-1 relative">
          <MediaDisplay
            mediaUrl={mediaUrl}
            altText="LinkedIn Shorts"
            type="video"
            aspectRatio="9/16"
            key={mediaUrl}
          />
          <div className="absolute right-3 bottom-4 flex flex-col items-center space-y-4">
            <button className="flex flex-col items-center space-y-0.5">
              <img src={linkedinLike} alt="Like" className="w-4 h-4" />
              <span className="text-[10px]">67.3k</span>
            </button>
            <button className="flex flex-col items-center space-y-0.5">
              <MessageCircle className="w-4 h-4" />
              <span className="text-[10px]">856</span>
            </button>
            <button className="flex flex-col items-center space-y-0.5">
              <Share2 className="w-4 h-4" />
              <span className="text-[10px]">43</span>
            </button>
            <button className="flex flex-col items-center">
              <Bookmark className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Caption */}
        <div className="absolute bottom-0 left-0 right-0 p-2.5 bg-gradient-to-t from-black/60 to-transparent">
          <div className="flex items-end justify-between">
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="h-4 w-4 rounded-full bg-gray-100 overflow-hidden">
                  {brand?.logo ? (
                    <img src={brand.logo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-200" />
                  )}
                </div>
                <span className="text-xs font-semibold">{brand?.name || 'Company Name'}</span>
              </div>
              <p className="text-xs" style={{ whiteSpace: 'pre-wrap' }}>{renderLinkedText(content)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};